import { LAMPORTS_PER_SOL } from "@solana/web3.js";
import { motion } from "framer-motion";
import { t } from "i18next";
import Image from "next/image";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { IoChevronForward } from "react-icons/io5";
import type { ListChildComponentProps } from "react-window";
import { FixedSizeList as List } from "react-window";

import SearchBar from "../SearchBar";
import SkeletonLoader from "../SkeletonLoader";
import { numberToShortVersion } from "@utils/number-to-short-version";
import { shortenAddress } from "@utils/shorten-address";
import { shortenName } from "@utils/shorten-string";
import { useAllValidators } from "hooks/useAllValidators";
import { useValidatorScores } from "hooks/useValidatorsScores";
import { ValidatorsSortingBy } from "services/marinade/fetchAllValidators";
import { MARINADE_STRATEGY } from "solana/ids";
import Images from "styles/image-library";
import type { ValidatorStats } from "types/validator-info";

export interface ValidatorSelectionBodyProps {
  preselectedOption: string;
  onValidatorSelection: (validatorVoteAccount?: string) => void;
}

export const ValidatorSelectionBody: FC<ValidatorSelectionBodyProps> = ({
  preselectedOption,
  onValidatorSelection,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const { numberOfDsConsideredValidators } = useValidatorScores();
  const { data, isLoading } = useAllValidators({
    limit: 9999,
    sortBy: ValidatorsSortingBy.MarinadeScore,
    searchTerm,
  });

  const [validators, setValidators] = useState<ValidatorStats[]>();
  const [selectedValidator, setSelectedValidator] = useState<ValidatorStats>();

  useEffect(() => {
    const validator = data?.validators.find(
      (v) => v.vote_account === preselectedOption
    );
    setSelectedValidator(validator);
    if (validator) {
      setValidators(
        data?.validators.filter((v) => v.vote_account !== preselectedOption)
      );
    }
  }, [preselectedOption, data?.validators]);

  useEffect(() => {
    if (preselectedOption) {
      setValidators(
        data?.validators.filter((v) => v.vote_account !== preselectedOption)
      );
    } else {
      setValidators(data?.validators);
    }
  }, [preselectedOption, data?.validators, searchTerm]);

  return (
    <>
      <div
        className={`w-full flex flex-col gap-3 rounded-[18px] px-3 py-4 mb-4 ${
          preselectedOption === MARINADE_STRATEGY.toString()
            ? "border-primary border bg-fade-green"
            : "bg-secondary-bg"
        }`}
      >
        <div
          role="button"
          tabIndex={0}
          className="w-full flex gap-3"
          onClick={() => {
            if (preselectedOption !== MARINADE_STRATEGY.toString())
              onValidatorSelection(MARINADE_STRATEGY.toString());
          }}
          onKeyDown={(e) => {
            if (
              (e.key === "Enter" || e.key === " ") &&
              preselectedOption !== MARINADE_STRATEGY.toString()
            ) {
              e.preventDefault();
              onValidatorSelection(MARINADE_STRATEGY.toString());
            }
          }}
        >
          <Image
            src={Images.icons_marinade_black}
            alt="mnde asset icon"
            width={36}
            height={36}
            className="rounded-full"
          />
          <div className="w-full flex flex-col">
            <span className="text-primary-text font-semibold">
              {`Marinade (${
                numberOfDsConsideredValidators || "100+"
              } validators)`}
            </span>
            <span className="text-secondary-text text-sm">
              {t("governance.marinade-option-details")}
            </span>
          </div>
          <IoChevronForward
            className={`w-5 h-5 min-w-[20px] text-tertiary-text self-center ${
              preselectedOption === MARINADE_STRATEGY.toString()
                ? "hidden"
                : "flex"
            }`}
          />
        </div>
        {preselectedOption === MARINADE_STRATEGY.toString() ? (
          <motion.button
            whileTap={{
              scale: 0.95,
              animationDuration: "0.2s",
            }}
            className="transition-all w-full flex h-10 gap-2 items-center justify-center rounded-full px-3 sm:px-4 font-semibold sm:font-bold focus:outline-none text-base border-primary border text-primary"
            onClick={() => onValidatorSelection()}
          >
            {t("governance.stop-directing")}
          </motion.button>
        ) : undefined}
      </div>
      <div className="flex items-center justify-center w-full">
        <div className="h-[1px] bg-gray-200 w-full" />
        <p className="px-1 w-fit whitespace-nowrap inline font-medium text-secondary-text">
          {t("governance.specific-validator")}
        </p>
        <div className="h-[1px] bg-gray-200 w-full" />
      </div>
      <SearchBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        className="my-3"
      />
      <div className="h-[40vh] overflow-y-auto overflow-x-hidden flex flex-col gap-1 md:mb-4">
        {preselectedOption &&
        preselectedOption !== MARINADE_STRATEGY.toString() &&
        !searchTerm &&
        !isLoading ? (
          <div className="w-full flex flex-col gap-3 border-primary border bg-fade-green rounded-[18px] px-3 py-4">
            <div className="flex gap-3">
              {selectedValidator?.info_keybase ? (
                <Image
                  src={`https://keybase.io/${selectedValidator.info_keybase}/picture?format=square_40`}
                  alt={`${selectedValidator.info_name} asset icon`}
                  width={36}
                  height={36}
                  className="rounded-full self-center"
                />
              ) : (
                <div className="w-9 h-9 bg-slate-400 rounded-full self-center" />
              )}
              <div className="w-full flex flex-col">
                <span className="text-primary-text font-semibold">
                  {selectedValidator?.info_name ??
                    shortenAddress(preselectedOption)}
                </span>
                <span className="text-secondary-text text-sm">
                  {`${numberToShortVersion(
                    Number(selectedValidator?.activated_stake) /
                      LAMPORTS_PER_SOL
                  )} SOL`}
                </span>
              </div>
            </div>
            <motion.button
              whileTap={{
                scale: 0.95,
                animationDuration: "0.2s",
              }}
              className="transition-all w-full flex h-10 gap-2 items-center justify-center rounded-full px-3 sm:px-4 font-semibold sm:font-bold focus:outline-none text-base border-primary border text-primary"
              onClick={() => onValidatorSelection()}
            >
              {t("governance.stop-directing")}
            </motion.button>
          </div>
        ) : undefined}
        {!!validators?.length && (
          <List
            height={910}
            itemCount={validators.length}
            itemSize={70}
            width="100%"
          >
            {({ index, style }: ListChildComponentProps) => {
              const validator = validators[index];
              return (
                <div
                  className="w-full flex gap-3 bg-secondary-bg rounded-[18px] px-3 py-4 items-center"
                  style={{
                    ...style,
                    height: (Number(style.height) ?? 0) - 5,
                    top: (Number(style.top) ?? 0) + 5,
                  }}
                  role="button"
                  tabIndex={index}
                  onClick={() => onValidatorSelection(validator.vote_account)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onValidatorSelection(validator.vote_account);
                    }
                  }}
                >
                  {validator.info_keybase ? (
                    <Image
                      src={`https://keybase.io/${validator.info_keybase}/picture?format=square_40`}
                      alt={`${validator.info_name} asset icon`}
                      width={36}
                      height={36}
                      className="rounded-full"
                    />
                  ) : (
                    <div className="w-9 h-9 min-w-9 bg-slate-400 rounded-full" />
                  )}
                  <div className="flex flex-col flex-1">
                    <span className="text-primary-text font-semibold whitespace-nowrap">
                      {validator.info_name
                        ? shortenName(validator.info_name)
                        : shortenAddress(validator.vote_account)}
                    </span>
                    <span className="text-secondary-text text-sm mb-1">
                      {`${numberToShortVersion(
                        Number(validator.activated_stake) / LAMPORTS_PER_SOL
                      )} SOL`}
                    </span>
                  </div>
                  <IoChevronForward className="w-5 h-5 min-w-[20px] text-tertiary-text" />
                </div>
              );
            }}
          </List>
        )}
        {!validators?.length &&
          (isLoading ? (
            <div>
              <SkeletonLoader
                isLoading={isLoading}
                className="h-[64px] w-full rounded-[18px]"
              />
              <SkeletonLoader
                isLoading={isLoading}
                className="mt-1 h-[64px] w-full rounded-[18px]"
              />
            </div>
          ) : (
            t("app.staking.selection.delegation.manual.no-validator")
          ))}
      </div>
    </>
  );
};
